/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { jsx, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import VideoStopOverlayIcon from '@atlaskit/icon/core/video-stop-overlay';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoThinkingIndicator } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingIndicator';
import { RainbowProgressBar } from '@atlassian/rovo-platform-ui-components/primitives/RainbowProgressBar';

const ROTATION_INTERVAL_MS = 30 * 1000;
const DEFAULT_ESTIMATED_DURATION_MS = 120 * 1000;
const COMPLETION_ANIMATION_MS = 150;

/**
 * Cycles through `texts` at a fixed interval, stopping on the last item.
 */
function useSequentialText(texts: string[]): string {
	const [index, setIndex] = useState(0);
	const indexRef = useRef(0);
	const textsRef = useRef(texts);
	textsRef.current = texts;

	useEffect(() => {
		if (textsRef.current.length <= 1) {
			return;
		}

		const interval = setInterval(() => {
			const next = indexRef.current + 1;
			if (next < textsRef.current.length) {
				indexRef.current = next;
				setIndex(next);
			} else {
				clearInterval(interval);
			}
		}, ROTATION_INTERVAL_MS);

		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
	}, []);

	return textsRef.current[index] ?? textsRef.current[0] ?? '';
}

/**
 * Smooth facade progress value (0-1): animates from 0 to ~90% over
 * `estimatedDurationMs`, then on `isComplete` rapidly fills to 1.0 and
 * fires `onCompleteAnimationFinished`.
 */
function useProgressFacade({
	isComplete,
	onCompleteAnimationFinished,
	estimatedDurationMs = DEFAULT_ESTIMATED_DURATION_MS,
}: {
	estimatedDurationMs?: number;
	isComplete: boolean;
	onCompleteAnimationFinished?: () => void;
}): number {
	const [progress, setProgress] = useState(0);
	const targetRef = useRef(0.85 + Math.random() * 0.1);
	const completionStartValueRef = useRef(0);
	const completionStartTimeRef = useRef<number | null>(null);
	const hasCalledFinishedRef = useRef(false);
	const onCompleteAnimationFinishedRef = useRef(onCompleteAnimationFinished);
	onCompleteAnimationFinishedRef.current = onCompleteAnimationFinished;

	useEffect(() => {
		if (isComplete) {
			return;
		}

		let rafId: number;
		let startTime: number | null = null;
		const target = targetRef.current;

		const tick = (timestamp: number) => {
			if (startTime === null) {
				startTime = timestamp;
			}
			const elapsed = timestamp - startTime;
			const linearProgress = Math.min(1, elapsed / estimatedDurationMs);
			setProgress(linearProgress * target);

			if (linearProgress < 1) {
				rafId = requestAnimationFrame(tick);
			}
		};

		rafId = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(rafId);
	}, [isComplete, estimatedDurationMs]);

	const handleCompletion = useCallback(() => {
		completionStartTimeRef.current = null;
		hasCalledFinishedRef.current = false;
		let rafId: number;

		const tick = (timestamp: number) => {
			if (completionStartTimeRef.current === null) {
				completionStartTimeRef.current = timestamp;
			}
			const elapsed = timestamp - completionStartTimeRef.current;
			const t = Math.min(1, elapsed / COMPLETION_ANIMATION_MS);
			const startValue = completionStartValueRef.current;
			const value = startValue + (1 - startValue) * t;
			setProgress(value);

			if (t < 1) {
				rafId = requestAnimationFrame(tick);
			} else if (!hasCalledFinishedRef.current) {
				hasCalledFinishedRef.current = true;
				onCompleteAnimationFinishedRef.current?.();
			}
		};

		rafId = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(rafId);
	}, []);

	useEffect(() => {
		if (!isComplete) {
			return;
		}
		completionStartValueRef.current = progress;
		const cleanup = handleCompletion();
		return cleanup;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only trigger when isComplete flips
	}, [isComplete, handleCompletion]);

	return progress;
}

const styles = cssMap({
	wrapper: {
		boxSizing: 'border-box',
		position: 'relative',
		borderRadius: token('radius.xxlarge'),
		height: '52px',
		display: 'flex',
		alignItems: 'center',
		backgroundColor: token('elevation.surface.overlay'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.100'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		gap: token('space.100'),
	},
	textCell: {
		flex: '1',
		width: '100%',
	},
	stopContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	escBadge: {
		paddingInline: token('space.050'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
		borderRadius: token('radius.small'),
	},
});

export type ThinkingProps = {
	/** When true, rapidly fills the progress bar to 100% then fires `onCompleteAnimationFinished`. */
	isComplete?: boolean;
	/** Fires once the progress bar finishes animating to 100%. */
	onCompleteAnimationFinished?: () => void;
	onStop?: () => void;
};

const StopIcon = () => <VideoStopOverlayIcon color={token('color.icon.danger')} label="" />;

export function Thinking({
	onStop,
	isComplete = false,
	onCompleteAnimationFinished,
}: ThinkingProps): JSX.Element {
	const { formatMessage } = useIntl();

	const thinkingTexts = [
		formatMessage(aiSuggestionsMessages.thinkingReviewingContent),
		formatMessage(aiSuggestionsMessages.thinkingCheckingRelatedSources),
		formatMessage(aiSuggestionsMessages.thinkingIdentifyingImprovements),
		formatMessage(aiSuggestionsMessages.thinkingPreparingSuggestions),
	];

	const thinkingTitle = useSequentialText(thinkingTexts);
	const progress = useProgressFacade({ isComplete, onCompleteAnimationFinished });

	const progressBar = (
		<RainbowProgressBar
			testId="ai-suggestions-thinking-progress-bar"
			progress={progress * 100}
			appearance="static-rainbow"
		/>
	);

	return (
		<div css={styles.wrapper}>
			<div css={styles.textCell}>
				<RovoThinkingIndicator
					title={thinkingTitle}
					animation
					bottomAdornment={progressBar}
					testId="ai-suggestions-thinking-indicator"
				/>
			</div>
			{onStop && (
				<div css={styles.stopContainer}>
					<span css={styles.escBadge}>
						<Text size="small" color="color.text.subtlest">
							{formatMessage(aiSuggestionsMessages.thinkingEscHint)}
						</Text>
					</span>
					<IconButton
						testId="ai-suggestions-thinking-stop-button"
						shape="circle"
						onClick={onStop}
						icon={StopIcon}
						label={formatMessage(aiSuggestionsMessages.thinkingStopLabel)}
					/>
				</div>
			)}
		</div>
	);
}
